import {
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IBreadcrumbEllipsisButtonProps from "./type";
import type {
    BreadcrumbEllipsisButtonWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useBreadcrumbContext
} from "../../context";
import {
    getBreadcrumbMeasures
} from "../../stylesheet";
import {
    BreadcrumbEllipsisIcon
} from "../../icons";
import StateLayer from "../../../stateLayer";

const BreadcrumbEllipsisButton: FC<IBreadcrumbEllipsisButtonProps> = ({
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "aria-haspopup": ariaHasPopup,
    customTheme: customThemeProp,
    isExpanded: isExpandedProp,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onBlur,
    style,
    title,
    ...props
}) => {
    const context = useBreadcrumbContext();

    const customTheme = customThemeProp ?? context.customTheme;

    const {
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isExpanded = isExpandedProp ?? !!ariaExpanded;

    const measures = getBreadcrumbMeasures({
        size: context.size,
        typography,
        iconSizes,
        spaces
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        pointerType: context.isCompact ? "coarse" : pointerType,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        measures,
        radiuses,
        borders,
        colors
    });

    const webProps: BreadcrumbEllipsisButtonWebPropsType = {
        "aria-haspopup": ariaHasPopup,
        "aria-controls": ariaControls
    };

    const isHighlighted = isHovered || isPressed || isExpanded;

    return <Pressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        aria-expanded={isExpanded}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={title}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isPressed={isPressed || isExpanded}
                borderRadius={radiuses.actions}
                customTheme={customTheme}
                isHovered={isHovered}
            />
            <BreadcrumbEllipsisIcon
                customColor={isHighlighted ? colors.content.icon.high : colors.content.icon.default}
                color={isHighlighted ? "high" : "default"}
                size={measures.iconSize}
            />
        </View>
    </Pressable>;
};
export default BreadcrumbEllipsisButton;
